import React from 'react';
import { Link } from 'react-router-dom';
import { BackButton } from '../../components/ui';

const ProhibitedItemsActivities = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gray-900 text-white py-4 px-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="w-3 h-3 bg-orange-500 rounded-full mr-3" aria-hidden="true"></div>
            <span className="text-lg font-semibold">Vesta • Legal Center</span>
          </div>
          <BackButton
            onClick={() => window.history.back()}
            variant="link"
            size="small"
            color="#C97B53"
            ariaLabel="Back to Legal Center"
          />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Policy Header */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Prohibited Items & Activities Policy
          </h1>
          <p className="text-gray-600 mb-4">
            Effective Date: [Insert Date]
          </p>
          <p className="text-gray-700">
            This policy outlines items and activities that are not permitted on Vesta, whether for sale, purchase, or as part of any service offered. Violations may result in listing removal, account suspension, permanent termination, and/or referral to law enforcement.
          </p>
        </div>

        {/* Policy Content */}
        <div className="space-y-8">
          {/* Section 1 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              1. Prohibited Food & Beverage Items
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">A. Foods prohibited under law:</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>Items banned by federal, state, or local laws where the seller operates or where the buyer is located.</li>
                  <li>Products not allowed under cottage food laws in the applicable jurisdiction.</li>
                  <li>Foods requiring commercial licensing that the seller does not hold.</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">B. Unsafe or high-risk foods:</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>Raw milk and raw milk products where prohibited.</li>
                  <li>Uninspected meats, certain wild game meats, or seafood where restricted.</li>
                  <li>Foods requiring refrigeration/hot-holding that cannot be safely delivered or picked up within legal time/temperature limits.</li>
                  <li>Expired or spoiled food.</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">C. Infused or controlled products:</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>Alcohol without the required licenses and permits.</li>
                  <li>Cannabis/THC-infused foods or beverages unless legal and licensed in both the seller's and buyer's jurisdictions.</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 2 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              2. Labeling & Disclosure Violations
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Selling food without legally required labels.</li>
              <li>Missing ingredient or allergen disclosures.</li>
              <li>Misrepresenting the production environment (e.g., stating "commercial kitchen" when prepared in a home kitchen).</li>
              <li>Omitting required cottage food disclaimers.</li>
            </ul>
          </section>

          {/* Section 3 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              3. Misleading, Unsafe, or Harmful Content
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Videos or images that depict unsafe food handling (e.g., cross-contamination, improper storage).</li>
              <li>False or unverified health/medical claims (e.g., "cures cancer," "treats chronic illness").</li>
              <li>Use of another seller's content without permission.</li>
              <li>Misleading edits, filters, or AI-generated images that misrepresent the actual product.</li>
            </ul>
          </section>

          {/* Section 4 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              4. Prohibited Services
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>In-home chef or catering services provided without necessary licensing or certifications.</li>
              <li>Services performed in unsafe premises or environments.</li>
              <li>Any service that violates health, labor, or safety laws.</li>
            </ul>
          </section>

          {/* Section 5 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              5. Prohibited Buyer & Seller Activities
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li><strong>Off-Platform Transactions</strong> – directing or encouraging transactions outside Vesta for items listed on the Platform.</li>
              <li><strong>Fraudulent Activity</strong> – using stolen payment methods, creating fake orders, or misrepresenting identity.</li>
              <li><strong>Review Manipulation</strong> – posting fake reviews, incentivizing reviews, or suppressing legitimate reviews.</li>
              <li><strong>Harassment & Abuse</strong> – threatening, harassing, or discriminating against other users.</li>
              <li><strong>Spam & Misrepresentation</strong> – posting irrelevant, duplicate, or deceptive listings.</li>
            </ul>
          </section>

          {/* Section 6 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              6. Legal Compliance Obligations
            </h2>
            <p className="text-gray-700 mb-3">Sellers and buyers must comply with all applicable:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Food safety laws.</li>
              <li>Licensing and permitting requirements.</li>
              <li>Labeling regulations.</li>
              <li>Business registration obligations.</li>
            </ul>
          </section>

          {/* Section 7 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              7. Consequences for Violations
            </h2>
            <p className="text-gray-700 mb-3">Depending on severity, Vesta may:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Remove listings or content.</li>
              <li>Issue warnings.</li>
              <li>Suspend or terminate accounts.</li>
              <li>Withhold funds pending resolution of disputes or legal claims.</li>
              <li>Report violations to authorities.</li>
            </ul>
          </section>

          {/* Section 8 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              8. Reporting Violations
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Users are encouraged to report suspected violations through the Platform's reporting system.</li>
              <li>All reports are reviewed by our compliance team.</li>
              <li>False reports may result in account suspension.</li>
              <li>Reports can be made anonymously, but providing contact information may help with investigation.</li>
            </ul>
          </section>

          {/* Section 9 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              9. Appeal Process
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Users may appeal enforcement actions within 30 days of receiving notice.</li>
              <li>Appeals must include relevant documentation and explanation.</li>
              <li>Appeals are reviewed by a different team member than the original decision.</li>
              <li>Final decisions are communicated within 14 business days.</li>
            </ul>
          </section>

          {/* Section 10 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              10. Policy Updates
            </h2>
            <p className="text-gray-700">
              We may update this policy at any time to reflect changes in law, safety guidance, or operational needs. Continued use of Vesta after updates constitutes acceptance.
            </p>
          </section>

          {/* Section 11 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              11. Contact Information
            </h2>
            <p className="text-gray-700 mb-4">
              For questions about this policy or to report violations, please contact us at:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Email: legal@vesta.com</li>
              <li>Compliance: compliance@vesta.com</li>
              <li>Phone: [Insert Phone Number]</li>
            </ul>
          </section>

          {/* Final Statement */}
          <section className="bg-red-50 border border-red-200 rounded-xl p-6">
            <p className="text-red-800 text-center font-medium">
              By using Vesta, you acknowledge that you have read, understood, and agree to comply with this Prohibited Items & Activities Policy.
            </p>
          </section>
        </div>

        {/* Footer Navigation */}
        <div className="mt-12 flex justify-center">
          <Link
            to="/legal"
            className="bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors"
            aria-label="Return to Legal Center"
          >
            Return to Legal Center
          </Link>
        </div>
      </main>
    </div>
  );
};

export default ProhibitedItemsActivities; 